"use client";

import React, { useCallback, useId, useMemo, useRef, useState } from "react";
import { styles } from "./PhoneInput.styles";
import { phoneCountryData, PhoneCountry } from "./phoneCountryData.generated";

export interface PhoneInputProps {
  value?: string;
  onChange: (value: string) => void;
  defaultCountry?: string;
  countryNames: Record<string, string>;
  label?: string;
  placeholder?: string;
  hasError?: boolean;
  message?: string;
  disabled?: boolean;
  testId?: string;
  id?: string;
  searchPlaceholder?: string;
  noResultsLabel?: string;
}

const DEFAULT_COUNTRY = "US";

const DEFAULT_PHONE_COUNTRY: Omit<PhoneCountry, "name"> = { code: "US", dialCode: "+1" };

const getCountryForCode = (code: string): Omit<PhoneCountry, "name"> =>
  phoneCountryData.find((c) => c.code === code) || DEFAULT_PHONE_COUNTRY;

export const PhoneInput = ({
  value = "",
  onChange,
  defaultCountry,
  countryNames,
  label,
  placeholder,
  hasError = false,
  message,
  disabled = false,
  testId,
  id,
  searchPlaceholder = "Search...",
  noResultsLabel = "No results",
}: PhoneInputProps) => {
  const generatedId = useId();
  const inputId = id || `phone-input-${generatedId}`;
  const initialCountry = getCountryForCode(defaultCountry || DEFAULT_COUNTRY);

  const [selectedCountry, setSelectedCountry] = useState(initialCountry);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [isFocused, setIsFocused] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const countries: PhoneCountry[] = useMemo(
    () =>
      phoneCountryData
        .map((c) => ({ ...c, name: countryNames[c.code] || c.code }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [countryNames],
  );

  const filteredCountries = useMemo(() => {
    if (!searchQuery) return countries;
    const query = (searchQuery ?? "").toLowerCase();
    return countries.filter(
      (c) => c.name.toLowerCase().includes(query) || c.dialCode.includes(query) || c.code.toLowerCase().includes(query),
    );
  }, [countries, searchQuery]);

  const stripDialCode = useCallback((val: string, country: Omit<PhoneCountry, "name">) => {
    if (!val) return "";
    if ((val ?? "").startsWith(country.dialCode)) {
      return val.slice(country.dialCode.length);
    }
    const match = phoneCountryData.find((c) => val.startsWith(c.dialCode));
    if (match) return val.slice(match.dialCode.length);
    return val.replace(/^\+\d*/, "");
  }, []);

  const phoneNumber = useMemo(() => stripDialCode(value, selectedCountry), [value, selectedCountry, stripDialCode]);

  const dialCodeDigits = selectedCountry.dialCode.replace(/\D/g, "").length;
  const maxLocalDigits = 15 - dialCodeDigits;
  const minLocalDigits = Math.max(0, 7 - dialCodeDigits);

  const handlePhoneChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const digits = e.target.value.replace(/\D/g, "");
      onChange(selectedCountry.dialCode + digits);
    },
    [onChange, selectedCountry],
  );

  const handleCountrySelect = useCallback(
    (country: Omit<PhoneCountry, "name">) => {
      setSelectedCountry(country);
      setIsDropdownOpen(false);
      setSearchQuery("");
      const localNumber = stripDialCode(value, selectedCountry);
      onChange(country.dialCode + localNumber);
      inputRef.current?.focus();
    },
    [onChange, value, selectedCountry, stripDialCode],
  );

  const handlePrefixClick = useCallback(() => {
    if (disabled) return;
    setIsDropdownOpen((prev) => {
      if (!prev) {
        setTimeout(() => {
          searchInputRef.current?.focus();
          const selectedOption = listRef.current?.querySelector<HTMLElement>("[aria-selected='true']");
          selectedOption?.scrollIntoView({ block: "nearest" });
        }, 0);
      }
      return !prev;
    });
  }, [disabled]);

  const handleBlur = useCallback((e: React.FocusEvent<HTMLDivElement>) => {
    const isStillWithin = containerRef.current?.contains(e.relatedTarget as Node);
    if (!isStillWithin) {
      setIsDropdownOpen(false);
      setSearchQuery("");
      setIsFocused(false);
    }
  }, []);

  const handleFocus = useCallback(() => {
    setIsFocused(true);
  }, []);

  const handleInputFocus = useCallback(() => {
    setIsDropdownOpen(false);
    setSearchQuery("");
  }, []);

  const handleKeyDownList = useCallback(
    (e: React.KeyboardEvent<HTMLUListElement>) => {
      const items = Array.from(listRef.current?.querySelectorAll<HTMLLIElement>("[role='option']") || []);
      const currentIndex = items.indexOf(document.activeElement as HTMLLIElement);

      if (e.key === "ArrowDown") {
        e.preventDefault();
        const next = currentIndex === -1 ? 0 : (currentIndex + 1) % items.length;
        (items[next] as HTMLElement)?.focus();
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        const prev = currentIndex === -1 ? items.length - 1 : (currentIndex - 1 + items.length) % items.length;
        (items[prev] as HTMLElement)?.focus();
      } else if (e.key === "Escape") {
        setIsDropdownOpen(false);
        setSearchQuery("");
        inputRef.current?.focus();
      }
    },
    [handleCountrySelect],
  );

  const handleSearchKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      const firstItem = listRef.current?.querySelector<HTMLElement>("[role='option']");
      firstItem?.focus();
    } else if (e.key === "Escape") {
      setIsDropdownOpen(false);
      setSearchQuery("");
      inputRef.current?.focus();
    }
  }, []);

  return (
    <div ref={containerRef} className={styles.root()} data-testid={testId} onBlur={handleBlur} onFocus={handleFocus}>
      {label && (
        <label htmlFor={inputId} className={styles.label(hasError)}>
          {label}
        </label>
      )}

      <div className={styles.inputWrapper()}>
        <div className={styles.container(hasError, disabled, isFocused)}>
          <button
            type="button"
            className={styles.prefixButton(disabled)}
            onClick={handlePrefixClick}
            disabled={disabled}
            aria-label="Select country code"
            aria-expanded={isDropdownOpen}
          >
            <span>{selectedCountry.dialCode}</span>
            <svg
              className={styles.prefixArrow(isDropdownOpen)}
              viewBox="0 0 12 10"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <polyline
                points="1 8 6 3 11 8"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>

          <input
            ref={inputRef}
            id={inputId}
            type="tel"
            inputMode="numeric"
            className={styles.input(disabled)}
            value={phoneNumber}
            onChange={handlePhoneChange}
            onFocus={handleInputFocus}
            placeholder={placeholder}
            disabled={disabled}
            minLength={minLocalDigits}
            maxLength={maxLocalDigits}
            aria-label={label || "Phone number"}
          />
        </div>

        {isDropdownOpen && (
          <div className={styles.dropdown()}>
            <input
              ref={searchInputRef}
              type="text"
              className={styles.searchInput()}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={handleSearchKeyDown}
              placeholder={searchPlaceholder}
              aria-label="Search countries"
            />
            <ul // NOSONAR - intentional: native select cannot render dial codes + country names with search
              ref={listRef}
              role="listbox"
              className={styles.dropdownList()}
              onKeyDown={handleKeyDownList}
              tabIndex={-1}
            >
              {filteredCountries.length === 0 ? (
                <li className={styles.noResults()}>{noResultsLabel}</li>
              ) : (
                filteredCountries.map((country) => (
                  <li
                    key={country.code}
                    role="option"
                    aria-selected={country.code === selectedCountry.code}
                    className={styles.option()}
                    tabIndex={-1}
                    data-country-code={country.code}
                    onClick={() => handleCountrySelect(country)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleCountrySelect(country);
                    }}
                  >
                    <span className={styles.optionDialCode()}>{country.dialCode}</span>
                    <span>{country.name}</span>
                  </li>
                ))
              )}
            </ul>
          </div>
        )}
      </div>

      {message && <div className={styles.message(hasError)}>{message}</div>}
    </div>
  );
};
